/**
 * Attendance view grouped by staff: folder cards + drill-down detail
 * with monthly bar chart and status pie chart.
 */
import { useMemo, useState } from 'react';
import {
  ArrowLeft, Users, Calendar, Clock, AlertTriangle, Upload, Plus,
  Search, ChevronRight, FileSpreadsheet, TrendingUp,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import CSVExportButton from '../CSVExportButton';

const STATUS_COLORS = {
  'Hadir': '#10b981',
  'Lewat': '#f59e0b',
  'MC': '#ef4444',
  'Cuti': '#3b82f6',
  'WFH': '#8b5cf6',
  'Outstation': '#14b8a6',
  'Tidak Hadir': '#6b7280',
};

const MONTHS_MY = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogo', 'Sep', 'Okt', 'Nov', 'Dis'];

// ---- helpers ----

const groupByStaff = (records = []) => {
  const map = new Map();
  for (const r of records) {
    const id = r.staff_id || r.staff_name || 'unknown';
    if (!map.has(id)) {
      map.set(id, {
        staff_id: r.staff_id,
        staff_name: r.staff_name || '(Tanpa nama)',
        department: r.department || '',
        records: [],
      });
    }
    map.get(id).records.push(r);
  }
  // Compute per-staff summary
  const out = [];
  for (const g of map.values()) {
    g.records.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    const total = g.records.length;
    const counts = {};
    let totalHours = 0;
    for (const r of g.records) {
      counts[r.status] = (counts[r.status] || 0) + 1;
      const h = Number(r.overtime_hours || 0) + 8;
      if (r.status === 'Hadir' || r.status === 'Lewat') totalHours += (Number.isFinite(h) ? h : 0);
    }
    g.summary = {
      total_days: total,
      hadir: counts['Hadir'] || 0,
      lewat: counts['Lewat'] || 0,
      mc: counts['MC'] || 0,
      cuti: counts['Cuti'] || 0,
      other: total - (counts['Hadir'] || 0) - (counts['Lewat'] || 0) - (counts['MC'] || 0) - (counts['Cuti'] || 0),
      total_hours: Math.round(totalHours * 10) / 10,
      latest_date: g.records[0]?.date || '',
    };
    out.push(g);
  }
  out.sort((a, b) => (a.staff_name || '').localeCompare(b.staff_name || ''));
  return out;
};

const monthlyBreakdown = (records) => {
  const buckets = {};
  for (const r of records) {
    if (!r.date) continue;
    const [y, m] = r.date.split('-');
    const key = `${y}-${m}`;
    if (!buckets[key]) {
      buckets[key] = { key, label: `${MONTHS_MY[parseInt(m, 10) - 1] || m} ${y.slice(-2)}`, Hadir: 0, Lewat: 0, MC: 0, Cuti: 0, WFH: 0, Lain: 0 };
    }
    const s = r.status;
    if (s in buckets[key]) buckets[key][s] += 1;
    else buckets[key].Lain += 1;
  }
  return Object.values(buckets).sort((a, b) => a.key.localeCompare(b.key));
};

const statusDistribution = (records) => {
  const counts = {};
  for (const r of records) {
    counts[r.status] = (counts[r.status] || 0) + 1;
  }
  return Object.entries(counts).map(([name, value]) => ({ name, value, color: STATUS_COLORS[name] || '#9ca3af' }));
};

// ---- small UI pieces ----

const Initial = ({ name }) => {
  const initials = (name || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-red-800 text-white flex items-center justify-center font-semibold text-sm shrink-0">
      {initials}
    </div>
  );
};

const StatPill = ({ label, value, tone = 'default' }) => {
  const tones = {
    default: 'bg-gray-100 text-gray-700',
    good: 'bg-emerald-100 text-emerald-800',
    warn: 'bg-amber-100 text-amber-800',
    bad: 'bg-rose-100 text-rose-800',
    info: 'bg-sky-100 text-sky-800',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${tones[tone]}`}>
      <span className="opacity-70">{label}</span>
      <span className="font-bold">{value}</span>
    </span>
  );
};

// ---- folder card ----

const StaffFolderCard = ({ group, onOpen }) => {
  const s = group.summary;
  return (
    <button
      onClick={() => onOpen(group)}
      className="group text-left bg-white border border-gray-200 hover:border-red-400 hover:shadow-md transition-all rounded-xl p-4 flex gap-3 items-start"
      data-testid={`staff-folder-${group.staff_id || group.staff_name}`}
    >
      <Initial name={group.staff_name} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-gray-800 truncate">{group.staff_name}</div>
            <div className="text-xs text-gray-500 truncate">{group.department || '—'}</div>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-red-600 shrink-0" />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          <StatPill label="Hari" value={s.total_days} tone="info" />
          <StatPill label="Hadir" value={s.hadir} tone="good" />
          {s.lewat > 0 && <StatPill label="Lewat" value={s.lewat} tone="warn" />}
          {s.mc > 0 && <StatPill label="MC" value={s.mc} tone="bad" />}
          {s.cuti > 0 && <StatPill label="Cuti" value={s.cuti} />}
        </div>
        <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-500">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {s.total_hours}h</span>
          {s.latest_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Akhir: {s.latest_date}</span>}
        </div>
      </div>
    </button>
  );
};

// ---- staff detail ----

const StaffDetail = ({ group, onBack, onEdit, onDelete }) => {
  const monthly = useMemo(() => monthlyBreakdown(group.records), [group.records]);
  const pie = useMemo(() => statusDistribution(group.records), [group.records]);
  const s = group.summary;

  return (
    <div className="space-y-4" data-testid="staff-attendance-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50"
          data-testid="attendance-back-btn"
          aria-label="Kembali"
        >
          <ArrowLeft className="w-4 h-4 text-gray-700" />
        </button>
        <Initial name={group.staff_name} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-800 truncate">{group.staff_name}</div>
          <div className="text-xs text-gray-500 truncate">{group.department || '—'} • {s.total_days} rekod kehadiran</div>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
        {[
          { label: 'Total Hari', value: s.total_days, tone: 'bg-sky-50 text-sky-800 border-sky-200' },
          { label: 'Hadir', value: s.hadir, tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
          { label: 'Lewat', value: s.lewat, tone: s.lewat ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-gray-50 text-gray-700 border-gray-200' },
          { label: 'MC / Cuti', value: s.mc + s.cuti, tone: 'bg-rose-50 text-rose-800 border-rose-200' },
          { label: 'Jumlah Jam', value: `${s.total_hours}h`, tone: 'bg-gray-50 text-gray-800 border-gray-200' },
        ].map((k) => (
          <div key={k.label} className={`border rounded-lg p-2.5 sm:p-3 ${k.tone}`}>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-wider opacity-70">{k.label}</div>
            <div className="text-lg sm:text-2xl font-semibold mt-0.5">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 sm:gap-4">
        <div className="lg:col-span-3 bg-white border border-gray-200 rounded-xl p-3 sm:p-4" data-testid="attendance-bar-chart">
          <div className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-2">
            <TrendingUp className="w-4 h-4" /> Breakdown Bulanan
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthly} margin={{ top: 10, right: 5, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" fontSize={10} />
              <YAxis fontSize={10} allowDecimals={false} width={30} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Hadir" stackId="a" fill={STATUS_COLORS.Hadir} />
              <Bar dataKey="Lewat" stackId="a" fill={STATUS_COLORS.Lewat} />
              <Bar dataKey="MC" stackId="a" fill={STATUS_COLORS.MC} />
              <Bar dataKey="Cuti" stackId="a" fill={STATUS_COLORS.Cuti} />
              <Bar dataKey="WFH" stackId="a" fill={STATUS_COLORS.WFH} />
              <Bar dataKey="Lain" stackId="a" fill="#9ca3af" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-3 sm:p-4" data-testid="attendance-pie-chart">
          <div className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-2">
            <Users className="w-4 h-4" /> Taburan Status
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={pie}
                dataKey="value"
                nameKey="name"
                innerRadius={40}
                outerRadius={75}
                paddingAngle={2}
                label={(e) => `${e.name} ${e.value}`}
              >
                {pie.map((p, i) => <Cell key={i} fill={p.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Records list - table on desktop, cards on mobile */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Calendar className="w-4 h-4" /> Senarai Kehadiran ({group.records.length})
        </div>

        {/* Desktop table */}
        <div className="hidden sm:block max-h-[480px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 sticky top-0 z-10">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Tarikh</th>
                <th className="text-center px-4 py-2 font-medium">Masuk</th>
                <th className="text-center px-4 py-2 font-medium">Keluar</th>
                <th className="text-center px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Notes</th>
                <th className="text-right px-4 py-2 font-medium">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {group.records.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50" data-testid={`attendance-row-${r.id}`}>
                  <td className="px-4 py-2">{r.date}</td>
                  <td className="px-4 py-2 text-center">{r.check_in || '—'}</td>
                  <td className="px-4 py-2 text-center">{r.check_out || '—'}</td>
                  <td className="px-4 py-2 text-center">
                    <span
                      className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium"
                      style={{
                        backgroundColor: (STATUS_COLORS[r.status] || '#9ca3af') + '22',
                        color: STATUS_COLORS[r.status] || '#4b5563',
                      }}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-600 max-w-xs truncate" title={r.notes}>{r.notes || '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => onEdit(r)}
                        className="text-xs text-sky-700 hover:underline px-1.5 py-1"
                        data-testid={`edit-attendance-${r.id}`}
                      >Edit</button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => onDelete(r.id)}
                        className="text-xs text-rose-700 hover:underline px-1.5 py-1"
                        data-testid={`delete-attendance-${r.id}`}
                      >Padam</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="sm:hidden max-h-[520px] overflow-y-auto divide-y divide-gray-100">
          {group.records.map((r) => (
            <div key={r.id} className="px-4 py-3" data-testid={`attendance-card-${r.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="font-medium text-gray-800 text-sm">{r.date}</span>
                <span
                  className="px-2 py-0.5 rounded-full text-[11px] font-medium"
                  style={{
                    backgroundColor: (STATUS_COLORS[r.status] || '#9ca3af') + '22',
                    color: STATUS_COLORS[r.status] || '#4b5563',
                  }}
                >
                  {r.status}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-600 mb-1 flex-wrap">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {r.check_in || '—'} → {r.check_out || '—'}
                </span>
                {r.source === 'self_service_gps' && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px] font-semibold uppercase tracking-wider border border-indigo-200">
                    📍 GPS
                  </span>
                )}
              </div>
              {r.notes && (
                <div className="text-[11px] text-gray-500 italic mt-1 line-clamp-2">{r.notes}</div>
              )}
              <div className="flex justify-end gap-4 mt-2">
                <button
                  onClick={() => onEdit(r)}
                  className="text-xs text-sky-700 font-medium min-h-[36px] px-2 flex items-center"
                  data-testid={`edit-attendance-m-${r.id}`}
                >Edit</button>
                <button
                  onClick={() => onDelete(r.id)}
                  className="text-xs text-rose-700 font-medium min-h-[36px] px-2 flex items-center"
                  data-testid={`delete-attendance-m-${r.id}`}
                >Padam</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---- main component ----

const AttendanceByStaff = ({ records, onAdd, onEdit, onDelete, onImport, importing }) => {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null); // YYYY-MM, null = semua

  const groups = useMemo(() => groupByStaff(records || []), [records]);

  // Available months across all records
  const availableMonths = useMemo(() => {
    const set = new Set();
    for (const r of records || []) {
      if (r.date && r.date.length >= 7) set.add(r.date.slice(0, 7));
    }
    return [...set].sort().reverse();
  }, [records]);

  // Auto-default to latest month on first load
  useMemo(() => {
    if (selectedMonth === null && availableMonths.length > 0) {
      setSelectedMonth(availableMonths[0]);
    }
  }, [availableMonths, selectedMonth]);

  // Monthly summary data (all staff, selected month)
  const monthSummary = useMemo(() => {
    const month = selectedMonth;
    const filteredRecords = month
      ? (records || []).filter(r => (r.date || '').startsWith(month))
      : (records || []);

    const statusCounts = {};
    const staffMap = new Map();
    let totalHours = 0;

    for (const r of filteredRecords) {
      statusCounts[r.status] = (statusCounts[r.status] || 0) + 1;
      const sid = r.staff_id || r.staff_name;
      if (!staffMap.has(sid)) {
        staffMap.set(sid, {
          staff_id: r.staff_id,
          staff_name: r.staff_name || '(Tanpa nama)',
          department: r.department || '',
          lewat: 0,
          hadir: 0,
          mc: 0,
          cuti: 0,
          days: 0,
        });
      }
      const s = staffMap.get(sid);
      s.days += 1;
      if (r.status === 'Lewat') s.lewat += 1;
      else if (r.status === 'Hadir') s.hadir += 1;
      else if (r.status === 'MC') s.mc += 1;
      else if (r.status === 'Cuti') s.cuti += 1;
      if (r.status === 'Hadir' || r.status === 'Lewat') {
        totalHours += 8 + Number(r.overtime_hours || 0);
      }
    }

    const pie = Object.entries(statusCounts)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value, color: STATUS_COLORS[name] || '#9ca3af' }));

    const topLewat = [...staffMap.values()]
      .filter(s => s.lewat > 0)
      .sort((a, b) => b.lewat - a.lewat)
      .slice(0, 5);

    return {
      total_records: filteredRecords.length,
      active_staff: staffMap.size,
      hadir: statusCounts['Hadir'] || 0,
      lewat: statusCounts['Lewat'] || 0,
      mc: statusCounts['MC'] || 0,
      cuti: statusCounts['Cuti'] || 0,
      total_hours: Math.round(totalHours * 10) / 10,
      pie,
      topLewat,
    };
  }, [records, selectedMonth]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = groups;
    if (q) {
      list = list.filter(g =>
        (g.staff_name || '').toLowerCase().includes(q) ||
        (g.department || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [groups, query]);

  const selected = selectedId ? groups.find(g => (g.staff_id || g.staff_name) === selectedId) : null;

  if (selected) {
    return (
      <StaffDetail
        group={selected}
        onBack={() => setSelectedId(null)}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="attendance-folders">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800">Kehadiran (Folder By Staff)</h3>
          <p className="text-xs text-gray-500">Klik mana-mana staf untuk tengok analisis & chart.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label
            className={`px-3 py-2 min-h-[44px] border border-emerald-600 text-emerald-700 rounded-lg flex items-center gap-2 text-sm cursor-pointer hover:bg-emerald-50 ${importing ? 'opacity-50' : ''}`}
            data-testid="attendance-import-label"
          >
            <Upload className="w-4 h-4" /> <span>Import</span>
            <input
              type="file"
              accept=".xlsx,.csv,.pdf"
              onChange={onImport}
              className="hidden"
              disabled={importing}
              data-testid="attendance-import-input"
            />
          </label>
          <CSVExportButton endpoint="/export/attendance.csv" filename="attendance" testId="export-attendance-csv" />
          <button
            onClick={onAdd}
            className="px-3 py-2 min-h-[44px] bg-red-700 text-white rounded-lg flex items-center gap-2 text-sm hover:bg-red-800"
            data-testid="attendance-add-btn"
          >
            <Plus className="w-4 h-4" /> <span>Tambah</span>
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs text-blue-700 flex items-start gap-2">
        <FileSpreadsheet className="w-4 h-4 shrink-0 mt-0.5" />
        <span>CSV/Excel: staff_name, date (YYYY-MM-DD), status, check_in, check_out, notes • PDF: Timesheet Report (auto-parse + analytics, lewat &gt; 9:30 AM)</span>
      </div>

      {/* ===== MONTHLY SUMMARY DASHBOARD (ALL STAFF) ===== */}
      {availableMonths.length > 0 && (
        <div className="bg-gradient-to-br from-red-50/60 to-white border border-red-200 rounded-xl p-4 space-y-3" data-testid="attendance-monthly-summary">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-red-700" />
              <h4 className="text-base font-semibold text-gray-800">Ringkasan Bulanan — Semua Staf</h4>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 font-medium">Bulan:</label>
              <select
                value={selectedMonth || ''}
                onChange={(e) => setSelectedMonth(e.target.value || null)}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-red-500"
                data-testid="attendance-month-selector"
              >
                <option value="">Semua</option>
                {availableMonths.map(m => {
                  const [y, mm] = m.split('-');
                  return <option key={m} value={m}>{MONTHS_MY[parseInt(mm, 10) - 1] || mm} {y}</option>;
                })}
              </select>
            </div>
          </div>

          {/* Summary stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {[
              { label: 'Staf Aktif', value: monthSummary.active_staff, tone: 'bg-sky-50 text-sky-800 border-sky-200' },
              { label: 'Total Rekod', value: monthSummary.total_records, tone: 'bg-gray-50 text-gray-800 border-gray-200' },
              { label: 'Hadir', value: monthSummary.hadir, tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
              { label: 'Lewat', value: monthSummary.lewat, tone: monthSummary.lewat ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-gray-50 text-gray-700 border-gray-200' },
              { label: 'MC / Cuti', value: monthSummary.mc + monthSummary.cuti, tone: 'bg-rose-50 text-rose-800 border-rose-200' },
              { label: 'Jumlah Jam', value: `${monthSummary.total_hours}h`, tone: 'bg-red-50 text-red-800 border-red-200' },
            ].map((k) => (
              <div key={k.label} className={`border rounded-lg p-2.5 ${k.tone}`} data-testid={`monthly-stat-${k.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="text-[10px] uppercase tracking-wider opacity-70">{k.label}</div>
                <div className="text-xl font-semibold mt-0.5">{k.value}</div>
              </div>
            ))}
          </div>

          {/* Charts row */}
          {monthSummary.total_records > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Pie: status distribution */}
              <div className="bg-white border border-gray-200 rounded-lg p-3" data-testid="monthly-pie-chart">
                <div className="flex items-center gap-1 text-xs font-semibold text-gray-700 mb-1">
                  <Users className="w-3.5 h-3.5" /> Taburan Status
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={monthSummary.pie}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={40}
                      outerRadius={75}
                      paddingAngle={2}
                      label={(e) => `${e.name} ${e.value}`}
                    >
                      {monthSummary.pie.map((p, i) => <Cell key={i} fill={p.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Top 5 Lewat staff */}
              <div className="bg-white border border-gray-200 rounded-lg p-3" data-testid="monthly-top-lewat">
                <div className="flex items-center gap-1 text-xs font-semibold text-gray-700 mb-1">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Top 5 Staf Lewat
                </div>
                {monthSummary.topLewat.length === 0 ? (
                  <div className="flex items-center justify-center h-[200px] text-xs text-gray-400">
                    Tiada staf lewat bulan ini 🎉
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={monthSummary.topLewat} layout="vertical" margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis type="number" allowDecimals={false} fontSize={11} />
                      <YAxis
                        type="category"
                        dataKey="staff_name"
                        width={120}
                        fontSize={10}
                        tick={{ fill: '#374151' }}
                      />
                      <Tooltip />
                      <Bar dataKey="lewat" fill={STATUS_COLORS.Lewat} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cari staf atau jabatan..."
          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-500"
          data-testid="attendance-search-input"
        />
      </div>

      {/* Folder grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-white border border-dashed border-gray-300 rounded-xl">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          {query ? 'Tiada staf padan dengan carian.' : 'Belum ada rekod kehadiran. Upload PDF Timesheet atau Tambah rekod baru.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(g => (
            <StaffFolderCard
              key={g.staff_id || g.staff_name}
              group={g}
              onOpen={(x) => setSelectedId(x.staff_id || x.staff_name)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AttendanceByStaff;
